import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Container, FlexContainer } from "../../styles/Layout";
import {
  FaLinkedin,
  FaTwitter,
  FaGithub,
  FaEnvelope,
  FaPhone,
  FaHeart,
  FaRocket,
} from "react-icons/fa";
import Logo from "../Logo/Logo";
import { Link } from "react-router-dom";

const FooterSection = styled.footer`
  background: linear-gradient(
    135deg,
    rgba(10, 10, 15, 0.95) 0%,
    rgba(26, 26, 46, 0.95) 100%
  );
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
  padding: var(--spacing-xxxl) 0 var(--spacing-lg);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
        circle at 20% 20%,
        rgba(0, 212, 255, 0.05) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 80%,
        rgba(255, 0, 128, 0.05) 0%,
        transparent 50%
      );
    z-index: 1;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: var(--spacing-xl);
  position: relative;
  z-index: 2;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-lg);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const CompanyInfo = styled(motion.div)`
  p {
    margin-bottom: var(--spacing-lg);
    color: var(--text-secondary);
    line-height: 1.7;
    font-size: 1.1rem;
  }
`;

const FooterTitle = styled.h4`
  margin-bottom: var(--spacing-lg);
  font-size: 1.3rem;
  position: relative;
  background: linear-gradient(
    135deg,
    var(--text-primary) 0%,
    var(--neon-blue) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 2px;
    background: linear-gradient(
      135deg,
      var(--neon-blue) 0%,
      var(--neon-magenta) 100%
    );
    border-radius: 1px;
  }

  @media (max-width: 600px) {
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const FooterLinks = styled.ul`
  list-style-type: none;

  li {
    margin-bottom: var(--spacing-md);
  }

  a {
    color: var(--text-secondary);
    transition: all 0.3s ease;
    font-size: 1rem;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: -15px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 2px;
      background: var(--neon-blue);
      transition: width 0.3s ease;
    }

    &:hover {
      color: var(--neon-blue);
      text-shadow: 0 0 10px currentColor;
      padding-left: 20px;

      &::before {
        width: 10px;
      }
    }
  }
`;

const ContactLinks = styled.div`
  margin-bottom: var(--spacing-lg);

  a {
    display: flex;
    align-items: center;
    margin-bottom: var(--spacing-md);
    color: var(--text-secondary);
    transition: all 0.3s ease;
    padding: var(--spacing-sm);
    border-radius: 8px;

    svg {
      margin-right: 12px;
      font-size: 1.1rem;
    }

    &:hover {
      color: var(--neon-blue);
      text-shadow: 0 0 10px currentColor;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.05) 0%,
        rgba(255, 255, 255, 0.02) 100%
      );
      transform: translateX(5px);
    }

    @media (max-width: 600px) {
      justify-content: center;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);

  @media (max-width: 600px) {
    justify-content: center;
  }
`;

const SocialLink = styled(motion.a)`
  width: 50px;
  height: 50px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.3s ease;
  font-size: 1.3rem;
  text-decoration: none;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      var(--neon-blue) 0%,
      var(--neon-magenta) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  svg {
    position: relative;
    z-index: 2;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 212, 255, 0.4);

    &::before {
      opacity: 1;
    }

    svg {
      color: white;
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(
    135deg,
    transparent 0%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 100%
  );
  margin: var(--spacing-xl) 0 var(--spacing-lg);
  position: relative;
  z-index: 2;
`;

const Copyright = styled(motion.div)`
  text-align: center;
  font-size: 1rem;
  color: var(--text-muted);
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);

  .heart {
    color: var(--neon-magenta);
    animation: heartbeat 2s ease-in-out infinite;
  }

  .rocket {
    color: var(--neon-blue);
    margin-left: var(--spacing-sm);
  }

  @keyframes heartbeat {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: var(--spacing-xs);
  }
`;

const Footer = () => {
  return (
    <FooterSection>
      <Container>
        <FooterContent>
          <CompanyInfo
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Logo light={true} size="large" />
            <p>
              We are a team of passionate developers and designers dedicated to
              creating cutting-edge digital solutions with premium design
              aesthetics that help businesses thrive in the modern world through
              innovative technology and exceptional user experiences.
            </p>
            <SocialLinks>
              <SocialLink
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaLinkedin />
              </SocialLink>
              <SocialLink
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTwitter />
              </SocialLink>
              <SocialLink
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaGithub />
              </SocialLink>
            </SocialLinks>
          </CompanyInfo>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <FooterTitle>Services</FooterTitle>
            <FooterLinks>
              <li>
                <a href="#services">Web Applications</a>
              </li>
              <li>
                <a href="#services">Mobile Apps</a>
              </li>
              <li>
                <a href="#services">ERP Systems</a>
              </li>
              <li>
                <a href="#services">Blockchain Solutions</a>
              </li>
              <li>
                <a href="#services">AI Integration</a>
              </li>
              <li>
                <a href="#services">Premium Design</a>
              </li>
            </FooterLinks>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLinks>
              <li>
                <a href="#home">Home</a>
              </li>
              <li>
                <a href="#about">About Us</a>
              </li>
              <li>
                <a href="#services">Services</a>
              </li>
              <li>
                <a href="#tech">Technology</a>
              </li>
              <li>
                <a href="#contact">Contact</a>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                >
                  Privacy Policy
                </Link>
              </li>
            </FooterLinks>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <FooterTitle>Contact Us</FooterTitle>
            <ContactLinks>
              <a href="mailto:support@geosentryx.com">
                <FaEnvelope /> support@geosentryx.com
              </a>
              <a href="tel:+919902691168">
                <FaPhone /> +91 99026-91168
              </a>
            </ContactLinks>
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "0.95rem",
                lineHeight: "1.6",
                textAlign: "center",
              }}
            >
              #15, 3rd Cross Jabbar Block,
              <br />
              PG Halli Vyalikaval Extn,
              <br />
              Bangalore North,
              <br />
              Karnataka - 560003 India
            </p>
          </motion.div>
        </FooterContent>

        <Divider />

        <Copyright
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <span>
            &copy; {new Date().getFullYear()}{" "}
            <img
              src={require("../../assets/logos/Labs_white.png")}
              alt="GeoX Labs Logo"
              style={{
                height: "20px",
                verticalAlign: "middle",
                marginRight: "5px",
              }}
            />
            . Made with
          </span>
          <FaHeart className="heart" />
          <span>and cutting-edge technology</span>
          <FaRocket className="rocket" />
        </Copyright>
      </Container>
    </FooterSection>
  );
};

export default Footer;
